import React, { useEffect, useState } from "react";
import axios from "axios";
import { XCircle } from "lucide-react";
import { API } from "@/config";
import { Card } from "@/components/admin/ui";

const REASON_LABEL = {
  illness: "Illness",
  weather: "Weather",
  plans_changed: "Plans changed",
  other: "Other",
  unspecified: "Not tagged",
};

const REASON_COLOR = {
  illness: "bg-sky-400",
  weather: "bg-red-400",
  plans_changed: "bg-[#FFD166]",
  other: "bg-fuchsia-400",
  unspecified: "bg-[#B9C8D5]/60",
};

export const CancelReasonStats = ({ days = 90 }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let ok = true;
    axios.get(`${API}/admin/cancel-reasons/stats`, { params: { days } })
      .then((r) => { if (ok) setData(r.data); })
      .catch(() => { if (ok) setData({ total: 0, rows: [] }); });
    return () => { ok = false; };
  }, [days]);

  const total = data?.total || 0;
  const rows = data?.rows || [];

  return (
    <Card>
      <div className="p-5" data-testid="cancel-reason-stats">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-300" />
            <h2 className="font-serif text-xl text-[#FFD166]">Cancellation reasons · last {days} days</h2>
          </div>
          <div className="text-xs text-[#B9C8D5]">
            <span className="uppercase tracking-wider">Total</span>{" "}
            <span className="text-[#F6F1E4] font-medium tabular-nums">{total}</span>
          </div>
        </div>
        <p className="text-[11px] text-[#B9C8D5] mt-1">
          Tag every cancellation from the "Cancel &amp; refund" modal so we can spot patterns and tune product copy.
        </p>

        {!data ? (
          <div className="mt-6 text-sm text-[#B9C8D5]">Loading…</div>
        ) : total === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center gap-1 text-center py-6 text-[#B9C8D5] text-sm" data-testid="cancel-reason-empty">
            <XCircle className="w-8 h-8 opacity-40" />
            <div>No cancellations tagged yet — pick a reason next time you cancel a booking.</div>
          </div>
        ) : (
          <div className="mt-4 space-y-2" data-testid="cancel-reason-rows">
            {rows.map((r) => {
              const pct = total ? (r.count / total) * 100 : 0;
              return (
                <div key={r.tag} className="rounded-lg border border-[#FFD166]/15 bg-black/25 p-3" data-testid={`cancel-reason-row-${r.tag}`}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-[#F6F1E4]">{REASON_LABEL[r.tag] || r.tag}</div>
                    <div className="text-[11px] text-[#B9C8D5] tabular-nums">
                      <span className="text-[#F6F1E4]">{r.count}</span> · {pct.toFixed(0)}%
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-black/40 overflow-hidden">
                    <div className={`${REASON_COLOR[r.tag] || "bg-[#B9C8D5]"} h-full`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CancelReasonStats;
